import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Col,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Tag,
  Table,
  Typography,
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import AdminLayout from '../components/AdminLayout';
import {
  getPreferredCustomizations,
  getPreferredMenuDishes,
} from '../services/posMergedDataService';
import { recipeService } from '../services/recipeService';
import { warehouseGoodsCatalogService } from '../services/warehouseGoodsCatalogService';
import {
  downloadCukcukRecipeExport,
  exportCukcukRecipesToFile,
} from '../services/cukcukRecipeExportService';
import { Customization } from '../types/customization';
import { OptionAddon, Recipe, RecipeIngredient } from '../types/recipe';
import {
  recipesNeedingPersist,
  syncRecipesToCukcukMenu,
  type RecipeSyncResult,
  type UnmatchedRecipeRef,
} from '../utils/recipeCukcukSync';
import { isBlockingCukcukRecipeWarning } from '../utils/cukcukRecipeExport';
import {
  findMenuMissingRecipes,
  findOrphanRecipesNotOnMenu,
  type MissingRecipeItem,
  type OrphanRecipeItem,
} from '../utils/recipeMenuCoverage';
import { filterRecipes } from '../utils/recipeSearch';
import '../styles/AdminPages.css';

const { Text } = Typography;

const CACHE_TTL_MS = 60 * 1000;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

let menuItemsCache: CacheEntry<InvoiceMenuItem[]> | null = null;
let customizationsCache: CacheEntry<Customization[]> | null = null;
let recipesCache: CacheEntry<Recipe[]> | null = null;

let menuItemsRequest: Promise<InvoiceMenuItem[]> | null = null;
let customizationsRequest: Promise<Customization[]> | null = null;
let recipesRequest: Promise<Recipe[]> | null = null;

const isCacheValid = <T,>(cache: CacheEntry<T> | null) =>
  !!cache && Date.now() - cache.timestamp < CACHE_TTL_MS;

interface InvoiceMenuItem {
  itemId: string;
  itemName: string;
  itemGroup?: string;
}

interface InvoiceOptionItem {
  optionId: string;
  optionName: string;
}

interface OptionAddonFormValues {
  optionId: string;
  optionName: string;
  ingredients: RecipeIngredient[];
}

interface RecipeFormValues {
  itemId: string;
  group?: string;
  instructions?: string;
  ingredients: RecipeIngredient[];
  optionAddons?: OptionAddonFormValues[];
}

const groupOptions = [
  { label: 'Arabica', value: 'Arabica' },
  { label: 'Robusta', value: 'Robusta' },
  { label: 'Cold brew', value: 'Cold brew' },
  { label: 'Non-cafein', value: 'Non-cafein' },
  { label: 'Chai', value: 'Chai' },
  { label: 'Nguyên liệu', value: 'Nguyên liệu' },
];

const RecipeManagement: React.FC = () => {
  const isMountedRef = useRef(true);
  const [menuItems, setMenuItems] = useState<InvoiceMenuItem[]>([]);
  const [customizations, setCustomizations] = useState<Customization[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [recipeSearch, setRecipeSearch] = useState('');
  const [quickPasteText, setQuickPasteText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [exportingCukcuk, setExportingCukcuk] = useState(false);
  const [menuSource, setMenuSource] = useState<'cukcuk' | 'ipos'>('cukcuk');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);
  const [unmatchedModalVisible, setUnmatchedModalVisible] = useState(false);
  const [unmatchedRecipes, setUnmatchedRecipes] = useState<UnmatchedRecipeRef[]>([]);
  const [syncedCount, setSyncedCount] = useState(0);
  const [missingRecipes, setMissingRecipes] = useState<MissingRecipeItem[]>([]);
  const [orphanRecipes, setOrphanRecipes] = useState<OrphanRecipeItem[]>([]);
  const [missingModalVisible, setMissingModalVisible] = useState(false);
  const [orphanModalVisible, setOrphanModalVisible] = useState(false);
  const [syncPreviewVisible, setSyncPreviewVisible] = useState(false);
  const [syncProposal, setSyncProposal] = useState<RecipeSyncResult | null>(null);
  const [form] = Form.useForm<RecipeFormValues>();
  const selectedItemId = Form.useWatch('itemId', form);
  const selectedGroup = Form.useWatch('group', form);
  const autoSyncDoneRef = useRef(false);

  const normalizeCode = (value?: string) => (value || '').trim().toLowerCase();
  const isSingleAmountMode = normalizeCode(selectedGroup) === 'chai';

  const extractOptionsFromCustomizations = (items: Customization[]): InvoiceOptionItem[] => {
    const seen = new Map<string, string>();
    items.forEach((customization) => {
      (customization.groups || []).forEach((group) => {
        (group.dishCodes || []).forEach((dish) => {
          const optionId = dish.code?.trim();
          const optionName = dish.name?.trim();
          if (!optionId || !optionName) return;
          if (dish.isActive === false) return;
          if (!seen.has(optionId)) seen.set(optionId, optionName);
        });
      });
    });

    return Array.from(seen.entries())
      .map(([optionId, optionName]) => ({ optionId, optionName }))
      .sort((a, b) => a.optionName.localeCompare(b.optionName, 'vi'));
  };

  const allCustomizationOptions = useMemo(
    () => extractOptionsFromCustomizations(customizations),
    [customizations]
  );

  const currentDishOptions = useMemo(() => {
    if (!selectedItemId) return [];
    const selectedCode = normalizeCode(selectedItemId);
    const matched = customizations.filter((customization) =>
      (customization.dishCode || []).some((dishCode) => normalizeCode(dishCode) === selectedCode)
    );
    return extractOptionsFromCustomizations(matched);
  }, [customizations, selectedItemId]);

  const selectableOptionItems = currentDishOptions.length
    ? currentDishOptions
    : allCustomizationOptions;

  // Menu ưu tiên CUKCUK (cùng món, đổi nguồn từ iPOS)
  const fetchMenuItems = useCallback(async (force = false) => {
    if (!force && isCacheValid(menuItemsCache)) {
      if (isMountedRef.current) {
        setMenuItems(menuItemsCache!.data);
      }
      return menuItemsCache!.data;
    }

    if (menuItemsRequest) {
      const sharedData = await menuItemsRequest;
      if (isMountedRef.current) {
        setMenuItems(sharedData);
      }
      return sharedData;
    }

    menuItemsRequest = (async () => {
      const { dishes, source } = await getPreferredMenuDishes();
      if (isMountedRef.current) {
        setMenuSource(source);
      }
      // Chỉ ghép công thức vào món bán thực tế. Món ảo/STPV là option phục vụ,
      // không phải đích để lưu công thức; giữ chúng trong customizations để
      // remap optionAddons riêng.
      return dishes
        .filter((dish) => !!dish.code && !!dish.name && !dish.isVirtualDish)
        .map((dish) => ({
          itemId: dish.code.trim(),
          itemName: dish.name.trim(),
          itemGroup: (dish.groupName || dish.group || '').trim(),
        }))
        .sort((a, b) => a.itemName.localeCompare(b.itemName, 'vi'));
    })();

    try {
      const data = await menuItemsRequest;
      menuItemsCache = { data, timestamp: Date.now() };
      if (isMountedRef.current) {
        setMenuItems(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching dishes:', error);
      if (isMountedRef.current) {
        message.error('Không thể tải danh sách món (CUKCUK / menu POS)');
      }
      return [];
    } finally {
      menuItemsRequest = null;
    }
  }, []);

  const fetchCustomizations = useCallback(async (force = false) => {
    if (!force && isCacheValid(customizationsCache)) {
      if (isMountedRef.current) {
        setCustomizations(customizationsCache!.data);
      }
      return customizationsCache!.data;
    }

    if (customizationsRequest) {
      const sharedData = await customizationsRequest;
      if (isMountedRef.current) {
        setCustomizations(sharedData);
      }
      return sharedData;
    }

    customizationsRequest = getPreferredCustomizations('cukcuk');

    try {
      const data = await customizationsRequest;
      customizationsCache = { data, timestamp: Date.now() };
      if (isMountedRef.current) {
        setCustomizations(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching customizations:', error);
      if (isMountedRef.current) {
        message.error('Không thể tải danh sách tùy chọn');
      }
      return [];
    } finally {
      customizationsRequest = null;
    }
  }, []);

  const fetchRecipes = useCallback(async (force = false) => {
    if (!force && isCacheValid(recipesCache)) {
      if (isMountedRef.current) {
        setRecipes(recipesCache!.data);
      }
      return recipesCache!.data;
    }

    if (recipesRequest) {
      const sharedData = await recipesRequest;
      if (isMountedRef.current) {
        setRecipes(sharedData);
      }
      return sharedData;
    }

    if (isMountedRef.current) {
      setLoading(true);
    }
    recipesRequest = recipeService.getAllRecipes();

    try {
      const data = await recipesRequest;
      recipesCache = { data, timestamp: Date.now() };
      if (isMountedRef.current) {
        setRecipes(data);
      }
      return data;
    } catch (error) {
      console.error('Error fetching recipes:', error);
      if (isMountedRef.current) {
        message.error('Không thể tải danh sách công thức');
      }
      return [];
    } finally {
      recipesRequest = null;
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  /** Lưu danh sách CT không map được. Chỉ mở popup khi openModal=true (bấm nút). */
  const storeUnmatchedRecipes = useCallback(
    (unmatched: UnmatchedRecipeRef[], synced: number, openModal = false) => {
      if (!isMountedRef.current) return;
      setUnmatchedRecipes(unmatched);
      setSyncedCount(synced);
      if (openModal && unmatched.length > 0) {
        setUnmatchedModalVisible(true);
      }
    },
    [],
  );

  const computeSyncProposal = useCallback(
    (
      currentRecipes: Recipe[],
      currentMenu: InvoiceMenuItem[],
      currentCustomizations: Customization[],
    ) => {
      const proposal = syncRecipesToCukcukMenu(
        currentRecipes,
        currentMenu.map((item) => ({
          code: item.itemId,
          name: item.itemName,
          group: item.itemGroup,
        })),
        currentCustomizations,
        { minScore: 680, minGap: 30 },
      );
      if (isMountedRef.current) {
        setSyncProposal(proposal);
        storeUnmatchedRecipes(proposal.unmatched, 0);
      }
      return proposal;
    },
    [storeUnmatchedRecipes],
  );

  const closeSyncPreview = useCallback(() => {
    setSyncPreviewVisible(false);
    setSyncProposal(null);
  }, []);

  const saveSyncProposal = async () => {
    if (!syncProposal || syncProposal.conflicts.length) return;

    setSyncing(true);
    try {
      const [freshMenu, freshCustomizations, freshRecipes] = await Promise.all([
        fetchMenuItems(true),
        fetchCustomizations(true),
        fetchRecipes(true),
      ]);
      const freshProposal = computeSyncProposal(freshRecipes, freshMenu, freshCustomizations);
      const toPersist = recipesNeedingPersist(freshRecipes, freshProposal.recipes);
      if (freshProposal.conflicts.length || toPersist.some((recipe) => !recipe.id)) {
        setSyncProposal(freshProposal);
        message.error('Dữ liệu đã thay đổi hoặc có công thức thiếu ID. Xem lại đề xuất mới.');
        return;
      }
      if (!toPersist.length) {
        closeSyncPreview();
        message.info('Không còn thay đổi để lưu.');
        return;
      }

      await recipeService.batchUpdateById(toPersist);
      const nextRecipes = await fetchRecipes(true);
      recipesCache = { data: nextRecipes, timestamp: Date.now() };
      setRecipes(nextRecipes);
      closeSyncPreview();
      storeUnmatchedRecipes(freshProposal.unmatched, toPersist.length);
      refreshCoverage(freshMenu, nextRecipes);
      message.success(`Đã đồng bộ ${toPersist.length} công thức với menu CUKCUK.`);
    } catch (error) {
      console.error('Error syncing recipes to CUKCUK:', error);
      message.error('Không thể đồng bộ công thức với menu CUKCUK.');
    } finally {
      setSyncing(false);
    }
  };

  const handleExportCukcukRecipes = useCallback(async (includeDraftMaterialMappings = false) => {
    setExportingCukcuk(true);
    try {
      const [menuResult, recipeList, materials, customizationList] = await Promise.all([
        getPreferredMenuDishes(),
        fetchRecipes(true),
        warehouseGoodsCatalogService.getAll(),
        fetchCustomizations(true),
      ]);
      const result = await exportCukcukRecipesToFile({
        dishes: menuResult.dishes,
        recipes: recipeList,
        materials,
        customizations: customizationList,
        includeDraftMaterialMappings,
      });
      downloadCukcukRecipeExport(result);

      if (result.warnings.length > 0) {
        const blockingWarnings = result.warnings.filter(isBlockingCukcukRecipeWarning);
        const informationalWarnings = result.warnings.filter(
          (warning) => !isBlockingCukcukRecipeWarning(warning),
        );
        const preview = [
          blockingWarnings.length
            ? `Cần xử lý trước khi nhập (${blockingWarnings.length}):\n${blockingWarnings
                .slice(0, 6)
                .map((warning) => `• ${warning.message}`)
                .join('\n')}${blockingWarnings.length > 6 ? '\n• ...' : ''}`
            : 'Không có cảnh báo chặn nhập.',
          informationalWarnings.length
            ? `Ghi chú tham khảo (${informationalWarnings.length}):\n${informationalWarnings
                .slice(0, 3)
                .map((warning) => `• ${warning.message}`)
                .join('\n')}${informationalWarnings.length > 3 ? '\n• ...' : ''}`
            : '',
        ]
          .filter(Boolean)
          .join('\n\n');
        Modal.warning({
          title: blockingWarnings.length
            ? `Đã xuất file — cần rà ${blockingWarnings.length} dòng`
            : 'Đã xuất file — không có cảnh báo chặn nhập',
          content: `${includeDraftMaterialMappings ? 'File FULL dùng các mã NVL bổ sung; hãy nhập CUKCUK_NVL_BO_SUNG_8AM.xls vào danh mục NVL trước, sau đó mới nhập file định lượng.' : ''}\nĐã ghi ${result.exportedIngredientRows} dòng định lượng.\n\n${preview}`,
          okText: 'Đã hiểu',
        });
      } else {
        message.success(`Đã xuất ${result.exportedIngredientRows} dòng định lượng CUKCUK.`);
      }
    } catch (error) {
      console.error('Error exporting CUKCUK recipe workbook:', error);
      message.error('Không thể xuất file định lượng CUKCUK. Vui lòng thử lại.');
    } finally {
      setExportingCukcuk(false);
    }
  }, [fetchCustomizations, fetchRecipes]);

  const refreshCoverage = useCallback(
    (currentMenu: InvoiceMenuItem[], currentRecipes: Recipe[], openMissingPopup = false) => {
      const dishRefs = currentMenu.map((item) => ({
        code: item.itemId,
        name: item.itemName,
        group: item.itemGroup,
      }));
      const missing = findMenuMissingRecipes(dishRefs, currentRecipes);
      const orphans = findOrphanRecipesNotOnMenu(dishRefs, currentRecipes);
      if (!isMountedRef.current) return;
      setMissingRecipes(missing);
      setOrphanRecipes(orphans);
      if (openMissingPopup && missing.length > 0) {
        setMissingModalVisible(true);
      }
    },
    [],
  );

  useEffect(() => {
    isMountedRef.current = true;
    autoSyncDoneRef.current = false;

    void (async () => {
      const [menu, customs, recipeList] = await Promise.all([
        fetchMenuItems(),
        fetchCustomizations(),
        fetchRecipes(),
      ]);

      if (!isMountedRef.current || autoSyncDoneRef.current) return;
      autoSyncDoneRef.current = true;

      // Chỉ tính đề xuất; không ghi công thức khi mở trang.
      if (menu.length > 0 && recipeList.length > 0) {
        computeSyncProposal(recipeList, menu, customs);
      }
      if (menu.length > 0) {
        refreshCoverage(menu, recipeList, true);
      }
    })();

    return () => {
      isMountedRef.current = false;
    };
  }, [computeSyncProposal, fetchCustomizations, fetchMenuItems, fetchRecipes, refreshCoverage]);

  // Gắn tên món vào công thức để hiện bảng
  const recipeRows = useMemo(() => {
    const nameMap = new Map(menuItems.map((m) => [m.itemId, m.itemName]));
    return recipes.map((r) => ({
      ...r,
      resolvedName: nameMap.get(r.itemId) || r.itemName,
    }));
  }, [recipes, menuItems]);
  const filteredRecipeRows = useMemo(
    () => filterRecipes(recipeRows, recipeSearch),
    [recipeRows, recipeSearch],
  );

  useEffect(() => {
    if (menuItems.length || recipes.length) {
      refreshCoverage(menuItems, recipes, false);
    }
  }, [menuItems, recipes, refreshCoverage]);

  const openAddModal = useCallback(
    (prefill?: { itemId: string; itemGroup?: string }) => {
      setEditingRecipe(null);
      form.resetFields();
      form.setFieldsValue({
        itemId: prefill?.itemId,
        group: prefill?.itemGroup || '',
        ingredients: [],
        optionAddons: [],
      });
      setModalVisible(true);
      setMissingModalVisible(false);
    },
    [form],
  );

  const openEditModal = useCallback((recipe: Recipe) => {
    setEditingRecipe(recipe);
    form.setFieldsValue({
      itemId: recipe.itemId,
      group: recipe.group,
      instructions: recipe.instructions,
      ingredients: recipe.ingredients || [],
      optionAddons: (recipe.optionAddons || []).map((a) => ({
        optionId: a.optionId,
        optionName: a.optionName,
        ingredients: a.ingredients || [],
      })),
    });
    setModalVisible(true);
  }, [form]);

  const openUnmatchedRecipe = useCallback(
    (row: UnmatchedRecipeRef) => {
      const recipe =
        recipes.find((item) => (row.id && item.id === row.id) || item.itemId === row.itemId) || null;
      if (!recipe) {
        message.warning('Không tìm thấy công thức trong danh sách hiện tại. Thử tải lại trang.');
        return;
      }
      openEditModal(recipe);
    },
    [openEditModal, recipes],
  );

  const copyUnmatchedList = useCallback(async () => {
    if (!unmatchedRecipes.length) {
      message.info('Không có món nào để sao chép.');
      return;
    }

    const header = ['STT', 'Mã món', 'Tên món', 'Nhóm', 'Số thành phần'].join('\t');
    const lines = unmatchedRecipes.map((row, index) =>
      [
        String(index + 1),
        row.itemId || '',
        row.itemName || '',
        row.group || '',
        String(row.ingredientCount ?? 0),
      ].join('\t'),
    );
    const text = [header, ...lines].join('\n');

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      message.success(`Đã sao chép ${unmatchedRecipes.length} món (dán vào Excel/Notepad được).`);
    } catch (error) {
      console.error('Copy unmatched list failed:', error);
      Modal.info({
        title: 'Sao chép thủ công',
        width: 640,
        content: (
          <Input.TextArea
            value={text}
            autoSize={{ minRows: 8, maxRows: 16 }}
            readOnly
            onFocus={(e) => e.target.select()}
          />
        ),
        okText: 'Đóng',
      });
    }
  }, [unmatchedRecipes]);

  const unmatchedColumns = useMemo(
    () => [
      {
        title: 'STT',
        key: 'index',
        width: 60,
        render: (_: unknown, __: UnmatchedRecipeRef, index: number) => index + 1,
      },
      {
        title: 'Mã món (công thức)',
        dataIndex: 'itemId',
        key: 'itemId',
        width: 160,
        ellipsis: true,
      },
      {
        title: 'Tên món (công thức)',
        dataIndex: 'itemName',
        key: 'itemName',
        ellipsis: true,
      },
      {
        title: 'Nhóm',
        dataIndex: 'group',
        key: 'group',
        width: 120,
        render: (value?: string) => value || '—',
      },
      {
        title: 'TP',
        dataIndex: 'ingredientCount',
        key: 'ingredientCount',
        width: 50,
        align: 'center' as const,
      },
      {
        title: 'Gợi ý CUKCUK (chưa đủ điểm)',
        key: 'suggestion',
        ellipsis: true,
        render: (_: unknown, record: UnmatchedRecipeRef) =>
          record.suggestion ? (
            <span>
              {record.suggestion.name}{' '}
              <Text type="secondary">
                ({record.suggestion.code} · {record.suggestion.score})
              </Text>
            </span>
          ) : (
            '—'
          ),
      },
      {
        title: 'Thao tác',
        key: 'action',
        width: 100,
        render: (_: unknown, record: UnmatchedRecipeRef) => (
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openUnmatchedRecipe(record)}>
            Sửa
          </Button>
        ),
      },
    ],
    [openUnmatchedRecipe],
  );

  const closeModal = useCallback(() => {
    setModalVisible(false);
    setEditingRecipe(null);
    form.resetFields();
    setQuickPasteText('');
  }, [form]);

  const parseQuickPasteIngredients = useCallback((raw: string, singleAmountMode: boolean): RecipeIngredient[] => {
    const rows = raw
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    return rows
      .map((line) => {
        const tabParts = line
          .split('\t')
          .map((part) => part.trim())
          .filter(Boolean);

        if (tabParts.length >= 3) {
          const [name, hotAmount, icedAmount] = tabParts;
          return {
            name,
            hotAmount: hotAmount || 'x',
            icedAmount: icedAmount || 'x',
          };
        }

        if (singleAmountMode && tabParts.length >= 2) {
          const [name, amount] = tabParts;
          return {
            name,
            hotAmount: amount || 'x',
            icedAmount: amount || 'x',
          };
        }

        const compactParts = line
          .split(/\s{2,}|\s*\|\s*|\s*;\s*|\s*,\s*/)
          .map((part) => part.trim())
          .filter(Boolean);

        if (compactParts.length >= 3) {
          const [name, hotAmount, icedAmount] = compactParts;
          return {
            name,
            hotAmount: hotAmount || 'x',
            icedAmount: icedAmount || 'x',
          };
        }

        if (singleAmountMode && compactParts.length >= 2) {
          const [name, amount] = compactParts;
          return {
            name,
            hotAmount: amount || 'x',
            icedAmount: amount || 'x',
          };
        }

        const match = line.match(/^(.*?)[\s\-–—:]+(-?\d+(?:[.,]\d+)?(?:\s*-\s*\d+(?:[.,]\d+)?)?|x|X)\s+(-?\d+(?:[.,]\d+)?(?:\s*-\s*\d+(?:[.,]\d+)?)?|x|X)$/);
        if (match) {
          return {
            name: match[1].trim(),
            hotAmount: match[2].trim().toLowerCase() === 'x' ? 'x' : match[2].trim(),
            icedAmount: match[3].trim().toLowerCase() === 'x' ? 'x' : match[3].trim(),
          };
        }

        if (singleAmountMode) {
          const singleMatch = line.match(/^(.*?)[\s\-–—:]+(-?\d+(?:[.,]\d+)?(?:\s*-\s*\d+(?:[.,]\d+)?)?|x|X)$/);
          if (singleMatch) {
            const amount = singleMatch[2].trim().toLowerCase() === 'x' ? 'x' : singleMatch[2].trim();
            return {
              name: singleMatch[1].trim(),
              hotAmount: amount,
              icedAmount: amount,
            };
          }
        }

        return null;
      })
      .filter((item): item is RecipeIngredient => !!item && !!item.name);
  }, []);

  const handleApplyQuickPasteIngredients = useCallback(() => {
    if (!quickPasteText.trim()) {
      message.warning('Vui lòng dán nội dung công thức trước');
      return;
    }

    const parsed = parseQuickPasteIngredients(quickPasteText, isSingleAmountMode);
    if (!parsed.length) {
      message.warning(isSingleAmountMode
        ? 'Không đọc được dữ liệu. Mỗi dòng cần có: Tên thành phần + Định lượng'
        : 'Không đọc được dữ liệu. Mỗi dòng cần có: Tên thành phần + Nóng + Đá');
      return;
    }

    form.setFieldsValue({ ingredients: parsed });
    message.success(`Đã dán nhanh ${parsed.length} thành phần`);
  }, [form, isSingleAmountMode, parseQuickPasteIngredients, quickPasteText]);

  const handlePrefillOptionsForDish = useCallback(() => {
    const itemId = form.getFieldValue('itemId') as string | undefined;
    if (!itemId) {
      message.warning('Vui lòng chọn món trước khi tạo option tự động');
      return;
    }

    if (!currentDishOptions.length) {
      message.info('Món này chưa có option trong trang Quản lý tùy chọn');
      return;
    }

    const existingAddons = (form.getFieldValue('optionAddons') || []) as OptionAddonFormValues[];
    const existingMap = new Map(existingAddons.map((addon) => [addon.optionId, addon]));

    const baseIngredients = (form.getFieldValue('ingredients') || []) as RecipeIngredient[];
    const normalizedBeanKeywords = ['hat', 'hạt', 'shot', 'espresso', 'coffee', 'robusta', 'arabica', 'blend'];
    const beanIngredient = baseIngredients.find((ingredient) => {
      const name = (ingredient?.name || '').toLowerCase();
      return normalizedBeanKeywords.some((keyword) => name.includes(keyword));
    }) || baseIngredients[0];

    const defaultHotAmount = (beanIngredient?.hotAmount || '').toString().trim() || 'x';
    const defaultIcedAmount = (beanIngredient?.icedAmount || '').toString().trim() || 'x';

    const mergedAddons: OptionAddonFormValues[] = currentDishOptions.map((opt) => {
      const existing = existingMap.get(opt.optionId);
      return existing || {
        optionId: opt.optionId,
        optionName: opt.optionName,
        ingredients: [
          {
            name: opt.optionName,
            hotAmount: defaultHotAmount,
            icedAmount: defaultIcedAmount,
          },
        ],
      };
    });

    form.setFieldsValue({ optionAddons: mergedAddons });
    message.success(`Đã tạo sẵn ${mergedAddons.length} option của món`);
  }, [currentDishOptions, form]);

  const handleSaveRecipe = useCallback(async (values: RecipeFormValues) => {
    const cleanIngredients = (list: RecipeIngredient[]) =>
      (list || []).map((item) => {
        const hotAmount = (item.hotAmount ?? '').toString().trim() || 'x';
        const icedAmountRaw = (item.icedAmount ?? '').toString().trim();
        const icedAmount = isSingleAmountMode ? hotAmount : (icedAmountRaw || 'x');
        return {
          name: item.name.trim(),
          hotAmount,
          icedAmount,
        };
      }).filter((item) => item.name);

    const cleanedIngredients = cleanIngredients(values.ingredients);

    if (!cleanedIngredients.length) {
      message.warning('Cần ít nhất 1 thành phần hợp lệ trong công thức');
      return;
    }

    const cleanedOptionAddons: OptionAddon[] = (values.optionAddons || [])
      .map((addon) => ({
        optionId: addon.optionId,
        optionName: addon.optionName ||
          selectableOptionItems.find((o) => o.optionId === addon.optionId)?.optionName ||
          addon.optionId,
        ingredients: cleanIngredients(addon.ingredients),
      }))
      .filter((addon) => addon.optionId && addon.ingredients.length > 0);

    const itemName =
      menuItems.find((m) => m.itemId === values.itemId)?.itemName || values.itemId;

    setSaving(true);
    try {
      const payload: Omit<Recipe, 'id'> = {
        itemId: values.itemId,
        itemIdAliases:
          editingRecipe && editingRecipe.itemId !== values.itemId
            ? Array.from(new Set([...(editingRecipe.itemIdAliases || []), editingRecipe.itemId]))
            : editingRecipe?.itemIdAliases,
        itemName,
        group: values.group?.trim() || '',
        instructions: values.instructions?.trim() || '',
        ingredients: cleanedIngredients,
        optionAddons: cleanedOptionAddons,
      };

      // Giữ id doc khi gán lại món CUKCUK (đổi itemId)
      const savedRecipe = await recipeService.saveRecipe(
        editingRecipe?.id ? { ...payload, id: editingRecipe.id } : payload,
      );
      const nowIso = new Date().toISOString();
      const mergedSavedRecipe: Recipe = {
        ...savedRecipe,
        createdAt: editingRecipe?.createdAt || nowIso,
        updatedAt: nowIso,
      };

      setRecipes((prev) => {
        const matchIndex = prev.findIndex(
          (recipe) =>
            (editingRecipe?.id && recipe.id === editingRecipe.id) ||
            recipe.id === mergedSavedRecipe.id ||
            recipe.itemId === mergedSavedRecipe.itemId
        );
        if (matchIndex === -1) {
          const next = [mergedSavedRecipe, ...prev];
          recipesCache = { data: next, timestamp: Date.now() };
          return next;
        }

        const next = [...prev];
        next[matchIndex] = {
          ...next[matchIndex],
          ...mergedSavedRecipe,
        };
        recipesCache = { data: next, timestamp: Date.now() };
        return next;
      });

      // Bỏ khỏi danh sách chưa map nếu đã gán sang món CUKCUK
      setUnmatchedRecipes((prev) =>
        prev.filter((row) => {
          if (editingRecipe?.id && row.id === editingRecipe.id) return false;
          if (row.itemId === editingRecipe?.itemId) return false;
          // Đã map nếu itemId mới nằm trong menu CUKCUK
          if (row.itemId === mergedSavedRecipe.itemId && menuItems.some((m) => m.itemId === mergedSavedRecipe.itemId)) {
            return false;
          }
          return true;
        }),
      );

      message.success('Đã lưu công thức thành công');
      closeModal();
    } catch (error) {
      console.error('Error saving recipe:', error);
      message.error('Không thể lưu công thức');
    } finally {
      setSaving(false);
    }
  }, [
    closeModal,
    editingRecipe,
    isSingleAmountMode,
    menuItems,
    selectableOptionItems,
  ]);

  const handleDelete = useCallback(async (recipe: Recipe) => {
    if (!recipe.id) return;
    try {
      await recipeService.deleteRecipe(recipe.id);
      message.success('Đã xóa công thức');
      setRecipes((prev) => {
        const next = prev.filter((r) => r.id !== recipe.id);
        recipesCache = { data: next, timestamp: Date.now() };
        return next;
      });
    } catch {
      message.error('Không thể xóa công thức');
    }
  }, []);

  const columns = useMemo(() => [
    {
      title: 'Nhóm đồ',
      dataIndex: 'group',
      key: 'group',
      render: (v: string) => v || '-',
      filters: Array.from(new Set(recipes.map((r) => r.group).filter(Boolean)))
        .sort((a, b) => String(a).localeCompare(String(b), 'vi'))
        .map((group) => ({ text: String(group), value: String(group) })),
      onFilter: (value: boolean | React.Key, record: Recipe) =>
        (record.group || '').toLowerCase() === String(value).toLowerCase(),
      sorter: (a: Recipe, b: Recipe) =>
        (a.group || '').localeCompare(b.group || '', 'vi'),
    },
    {
      title: 'Tên món',
      key: 'resolvedName',
      render: (_: unknown, record: Recipe & { resolvedName: string }) => record.resolvedName,
      sorter: (a: Recipe & { resolvedName: string }, b: Recipe & { resolvedName: string }) =>
        (a.resolvedName || '').localeCompare(b.resolvedName || '', 'vi'),
      sortDirections: ['ascend', 'descend'] as ('ascend' | 'descend')[],
    },
    {
      title: 'Mã món (ID)',
      dataIndex: 'itemId',
      key: 'itemId',
      sorter: (a: Recipe, b: Recipe) =>
        (a.itemId || '').localeCompare(b.itemId || '', 'vi'),
    },
    {
      title: 'Số thành phần',
      key: 'ingredientCount',
      render: (_: unknown, record: Recipe) => record.ingredients?.length || 0,
      sorter: (a: Recipe, b: Recipe) =>
        (a.ingredients?.length || 0) - (b.ingredients?.length || 0),
    },
    {
      title: 'Cập nhật',
      key: 'updatedAt',
      render: (_: unknown, record: Recipe) =>
        record.updatedAt ? new Date(record.updatedAt).toLocaleString('vi-VN') : '-',
      sorter: (a: Recipe, b: Recipe) => {
        const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return aTime - bTime;
      },
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Thao tác',
      key: 'action',
      width: 160,
      render: (_: unknown, record: Recipe) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditModal(record)}>
            Sửa
          </Button>
          <Popconfirm
            title="Xóa công thức này?"
            onConfirm={() => handleDelete(record)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ], [handleDelete, openEditModal, recipes]);

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1>Quản lý công thức món</h1>
          <Space size={8} wrap>
            <Tag color="blue">{recipes.length} công thức</Tag>
            <Tag color={menuSource === 'cukcuk' ? 'green' : 'orange'}>
              Menu: {menuSource === 'cukcuk' ? 'CUKCUK' : 'iPOS (fallback)'}
            </Tag>
            {missingRecipes.length > 0 && (
              <Tag color="gold" style={{ cursor: 'pointer' }} onClick={() => setMissingModalVisible(true)}>
                {missingRecipes.length} món CUKCUK thiếu CT
              </Tag>
            )}
            {orphanRecipes.length > 0 && (
              <Tag color="default" style={{ cursor: 'pointer' }} onClick={() => setOrphanModalVisible(true)}>
                {orphanRecipes.length} CT bỏ qua (không trên menu)
              </Tag>
            )}
          </Space>
        </div>

        <div className="admin-page-actions">
          {missingRecipes.length > 0 && (
            <Button
              type="primary"
              ghost
              onClick={() => setMissingModalVisible(true)}
              className="admin-button"
            >
              Thiếu CT ({missingRecipes.length})
            </Button>
          )}
          {orphanRecipes.length > 0 && (
            <Button onClick={() => setOrphanModalVisible(true)} className="admin-button">
              Bỏ qua ({orphanRecipes.length})
            </Button>
          )}
          <Button
            type="primary"
            icon={<SyncOutlined />}
            loading={syncing}
            onClick={() => {
              if (!recipes.length || !menuItems.length) {
                message.info('Chưa có công thức hoặc menu CUKCUK để đồng bộ.');
                return;
              }
              computeSyncProposal(recipes, menuItems, customizations);
              setSyncPreviewVisible(true);
            }}
            className="admin-button"
          >
            Xem trước ghép CUKCUK
          </Button>
          <Button
            icon={<DownloadOutlined />}
            loading={exportingCukcuk}
            onClick={() => void handleExportCukcukRecipes()}
            className="admin-button"
          >
            Xuất định lượng CUKCUK
          </Button>
          <Button
            icon={<DownloadOutlined />}
            loading={exportingCukcuk}
            onClick={() => void handleExportCukcukRecipes(true)}
            className="admin-button"
          >
            Xuất đầy đủ (mã NVL bổ sung)
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              menuItemsCache = null;
              customizationsCache = null;
              recipesCache = null;
              autoSyncDoneRef.current = false;
              void (async () => {
                const [menu, customs, recipeList] = await Promise.all([
                  fetchMenuItems(true),
                  fetchCustomizations(true),
                  fetchRecipes(true),
                ]);
                autoSyncDoneRef.current = true;
                computeSyncProposal(recipeList, menu, customs);
                refreshCoverage(menu, recipeList);
              })();
            }}
            className="admin-button"
          >
            Tải lại
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openAddModal()}
            className="admin-button"
          >
            Thêm công thức
          </Button>
        </div>
      </div>

      <div className="admin-table-container">
        <Input.Search
          allowClear
          value={recipeSearch}
          onChange={(event) => setRecipeSearch(event.target.value)}
          placeholder="Tìm theo tên món, mã món hoặc nhóm"
          aria-label="Tìm công thức theo tên hoặc mã món"
          style={{ width: 360, maxWidth: '100%', marginBottom: 16 }}
        />
        <Table
          className="admin-table"
          columns={columns}
          dataSource={filteredRecipeRows}
          loading={loading}
          rowKey="id"
          locale={{ emptyText: <Empty description="Chưa có công thức nào" /> }}
          pagination={{ defaultPageSize: 10, responsive: true }}
        />
      </div>

      <Modal
        title="Xem trước ghép CUKCUK"
        open={syncPreviewVisible}
        onCancel={closeSyncPreview}
        width={1100}
        footer={[
          <Button key="cancel" onClick={closeSyncPreview}>Hủy</Button>,
          <Button
            key="save"
            type="primary"
            icon={<SaveOutlined />}
            loading={syncing}
            disabled={
              !syncProposal ||
              syncProposal.conflicts.length > 0 ||
              recipesNeedingPersist(recipes, syncProposal.recipes).length === 0
            }
            onClick={() => void saveSyncProposal()}
          >
            Lưu đồng bộ
          </Button>,
        ]}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {syncProposal?.conflicts.length ? (
            <Text type="danger">
              Có {syncProposal.conflicts.length} xung đột ID. Cần sửa xung đột trước khi lưu.
            </Text>
          ) : null}
          <Table
            size="small"
            rowKey={(row) => row.recipeId || `${row.previousItemId}-${row.nextItemId}`}
            dataSource={syncProposal?.changed || []}
            pagination={false}
            scroll={{ x: 950, y: 320 }}
            locale={{ emptyText: 'Không có thay đổi' }}
            columns={[
              { title: 'ID cũ', dataIndex: 'previousItemId', width: 130 },
              { title: 'Tên cũ', dataIndex: 'previousItemName', ellipsis: true },
              { title: 'ID mới', dataIndex: 'nextItemId', width: 130 },
              { title: 'Tên mới', dataIndex: 'nextItemName', ellipsis: true },
              { title: 'Cách ghép', dataIndex: 'matchBy', width: 110 },
              { title: 'Điểm', dataIndex: 'score', width: 70 },
              {
                title: 'Alias thêm',
                width: 130,
                render: (_: unknown, row) => row.previousItemId !== row.nextItemId ? row.previousItemId : '—',
              },
              { title: 'Option đổi', dataIndex: 'optionAddonsRemapped', width: 100 },
            ]}
          />
          {syncProposal?.conflicts.length ? (
            <Table
              size="small"
              rowKey={(row) => `${row.recipeId || row.previousItemId}-${row.targetItemId}`}
              dataSource={syncProposal.conflicts}
              pagination={false}
              columns={[
                { title: 'ID cũ', dataIndex: 'previousItemId', width: 130 },
                { title: 'Tên cũ', dataIndex: 'previousItemName' },
                { title: 'ID mới', dataIndex: 'targetItemId', width: 130 },
                { title: 'Tên mới', dataIndex: 'nextItemName' },
                { title: 'Cách ghép', dataIndex: 'matchBy', width: 100 },
                { title: 'Điểm', dataIndex: 'score', width: 70 },
                { title: 'Alias thêm', dataIndex: 'previousItemId', width: 130 },
                { title: 'Option đổi', dataIndex: 'optionAddonsRemapped', width: 100 },
                { title: 'Xung đột', dataIndex: 'type', width: 130 },
              ]}
            />
          ) : null}
        </Space>
      </Modal>

      <Modal
        title={`Món CUKCUK chưa có công thức (${missingRecipes.length}) — nên thêm`}
        open={missingModalVisible}
        onCancel={() => setMissingModalVisible(false)}
        width={900}
        footer={[
          <Button
            key="copy"
            icon={<CopyOutlined />}
            onClick={async () => {
              const text = [
                ['STT', 'Mã món', 'Tên món', 'Nhóm'].join('\t'),
                ...missingRecipes.map((row, i) =>
                  [String(i + 1), row.code, row.name, row.group || ''].join('\t'),
                ),
              ].join('\n');
              try {
                await navigator.clipboard.writeText(text);
                message.success(`Đã chép ${missingRecipes.length} món thiếu CT`);
              } catch {
                message.error('Không chép được clipboard');
              }
            }}
            disabled={!missingRecipes.length}
          >
            Sao chép danh sách
          </Button>,
          <Button key="close" type="primary" onClick={() => setMissingModalVisible(false)}>
            Đóng
          </Button>,
        ]}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            Đây là món <strong>có trên thực đơn CUKCUK</strong> nhưng <strong>chưa có công thức</strong>.
            Xuất nguyên liệu sẽ bỏ qua các món này cho đến khi bạn thêm CT. Bấm <strong>Thêm CT</strong> để tạo nhanh.
          </div>
          <Table
            size="small"
            rowKey={(row) => row.code}
            dataSource={missingRecipes}
            pagination={missingRecipes.length > 15 ? { pageSize: 15 } : false}
            scroll={{ y: 400 }}
            columns={[
              {
                title: 'STT',
                width: 55,
                render: (_: unknown, __: MissingRecipeItem, index: number) => index + 1,
              },
              { title: 'Mã món', dataIndex: 'code', width: 150, ellipsis: true },
              { title: 'Tên món', dataIndex: 'name', ellipsis: true },
              {
                title: 'Nhóm',
                dataIndex: 'group',
                width: 140,
                render: (v?: string) => v || '—',
              },
              {
                title: 'Thao tác',
                width: 110,
                render: (_: unknown, record: MissingRecipeItem) => (
                  <Button
                    type="link"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => openAddModal({ itemId: record.code, itemGroup: record.group })}
                  >
                    Thêm CT
                  </Button>
                ),
              },
            ]}
          />
        </Space>
      </Modal>

      <Modal
        title={`Công thức có thể bỏ qua (${orphanRecipes.length}) — không còn trên menu CUKCUK`}
        open={orphanModalVisible}
        onCancel={() => setOrphanModalVisible(false)}
        width={860}
        footer={[
          <Button key="close" type="primary" onClick={() => setOrphanModalVisible(false)}>
            Đã hiểu — bỏ qua
          </Button>,
        ]}
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <div>
            Công thức còn trong hệ thống nhưng <strong>không có trên thực đơn CUKCUK</strong> (món
            ngừng bán / đổi mã). <strong>Không cần map</strong>, không ảnh hưởng xuất NL CUKCUK. Có
            thể xóa sau nếu muốn dọn.
          </div>
          <Table
            size="small"
            rowKey={(row) => row.id || row.itemId}
            dataSource={orphanRecipes}
            pagination={orphanRecipes.length > 15 ? { pageSize: 15 } : false}
            scroll={{ y: 360 }}
            columns={[
              {
                title: 'STT',
                width: 55,
                render: (_: unknown, __: OrphanRecipeItem, index: number) => index + 1,
              },
              { title: 'Mã CT', dataIndex: 'itemId', width: 160, ellipsis: true },
              { title: 'Tên CT', dataIndex: 'itemName', ellipsis: true },
              {
                title: 'Nhóm',
                dataIndex: 'group',
                width: 120,
                render: (v?: string) => v || '—',
              },
              { title: 'TP', dataIndex: 'ingredientCount', width: 50 },
            ]}
          />
        </Space>
      </Modal>

      {/* Popup map mơ hồ chỉ mở khi user bấm tag/nút — không auto lúc load */}
      <Modal
        title={`CT không trên menu CUKCUK (${unmatchedRecipes.length}) — bỏ qua`}
        open={unmatchedModalVisible}
        onCancel={() => setUnmatchedModalVisible(false)}
        width={860}
        footer={[
          <Button
            key="copy"
            icon={<CopyOutlined />}
            onClick={() => void copyUnmatchedList()}
            disabled={!unmatchedRecipes.length}
          >
            Sao chép danh sách
          </Button>,
          <Button key="close" type="primary" onClick={() => setUnmatchedModalVisible(false)}>
            Đã hiểu — bỏ qua
          </Button>,
        ]}
      >
        <Space direction="vertical" size={12} style={{ width: '100%', userSelect: 'text' }}>
          <div>
            {syncedCount > 0 ? (
              <span>
                Đã tự ghép <strong>{syncedCount}</strong> công thức.{' '}
              </span>
            ) : null}
            Các CT dưới đây <strong>không có trên thực đơn CUKCUK</strong> (ngừng bán / mã cũ).{' '}
            <strong>Không cần map</strong>, không chặn xuất NL. Dùng nút <strong>Bỏ qua</strong> trên
            toolbar cũng xem được danh sách tương tự.
          </div>
          <Table
            size="small"
            rowKey={(row) => row.id || row.itemId}
            columns={unmatchedColumns}
            dataSource={unmatchedRecipes}
            pagination={unmatchedRecipes.length > 12 ? { pageSize: 12 } : false}
            scroll={{ y: 360 }}
            locale={{ emptyText: 'Không còn món nào' }}
            style={{ userSelect: 'text' }}
          />
        </Space>
      </Modal>

      <Modal
        title={editingRecipe ? 'Sửa công thức món' : 'Thêm công thức mới'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={920}
        destroyOnClose
      >
        <Form<RecipeFormValues>
          form={form}
          layout="vertical"
          size="small"
          onFinish={handleSaveRecipe}
          initialValues={{ ingredients: [] }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={16}>
              <Form.Item
                label={
                  <Space size={8}>
                    <span>Tên món</span>
                    {editingRecipe &&
                    menuItems.some((m) => m.itemId === editingRecipe.itemId) ? (
                      <Tag color="success">Đã map CUKCUK</Tag>
                    ) : editingRecipe ? (
                      <Tag color="error">Chưa map — chọn món CUKCUK bên dưới</Tag>
                    ) : (
                      <Tag color="blue">Menu CUKCUK</Tag>
                    )}
                  </Space>
                }
                name="itemId"
                rules={[{ required: true, message: 'Vui lòng chọn món' }]}
                extra={
                  editingRecipe &&
                  !menuItems.some((m) => m.itemId === editingRecipe.itemId) ? (
                    <span>
                      Mã hiện tại <Text code>{editingRecipe.itemId}</Text> không có trên menu
                      CUKCUK. Mở dropdown → chọn dòng có prefix <strong>[CUKCUK]</strong> (tìm
                      theo tên món) → Lưu. Không chọn dòng <strong>[CHƯA MAP]</strong>.
                    </span>
                  ) : (
                    'Danh sách món lấy từ import CUKCUK (cukcuk_dishes).'
                  )
                }
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Tìm và chọn món trên menu CUKCUK"
                  // Cho đổi món nếu CT chưa map CUKCUK (để gán lại sang mã đúng)
                  disabled={
                    !!editingRecipe &&
                    menuItems.some((m) => m.itemId === editingRecipe.itemId)
                  }
                  onChange={(value) => {
                    const selected = menuItems.find((m) => m.itemId === value);
                    if (selected?.itemGroup) {
                      form.setFieldsValue({ group: selected.itemGroup });
                    }
                  }}
                  filterOption={(input, option) => {
                    const q = input.trim().toLowerCase();
                    if (!q) return true;
                    const haystack = String(option?.label ?? '').toLowerCase();
                    return haystack.includes(q);
                  }}
                  options={(() => {
                    const cukcukOptions = menuItems.map((m) => ({
                      value: m.itemId,
                      label: `${m.itemName} (${m.itemId})`,
                    }));

                    const isUnmapped =
                      !!editingRecipe?.itemId &&
                      !cukcukOptions.some((opt) => opt.value === editingRecipe.itemId);

                    if (!isUnmapped) {
                      return cukcukOptions;
                    }

                    // Flat list + prefix rõ ràng (tránh type OptGroup gây lỗi filterOption)
                    return [
                      {
                        value: editingRecipe!.itemId,
                        label: `[CHƯA MAP] ${editingRecipe!.itemName || editingRecipe!.itemId} (${editingRecipe!.itemId})`,
                      },
                      ...cukcukOptions.map((opt) => ({
                        ...opt,
                        label: `[CUKCUK] ${opt.label}`,
                      })),
                    ];
                  })()}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Nhóm đồ" name="group">
                <Select
                  showSearch
                  allowClear
                  placeholder="Arabica, Robusta, Cold brew..."
                  options={groupOptions}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Ghi chú pha chế" name="instructions" style={{ marginBottom: 12 }}>
            <Input.TextArea
              rows={2}
              placeholder="Ví dụ: Đổ đá, sữa, rồi chiết cà phê lên trên. Thêm ống hút nhỏ."
            />
          </Form.Item>

          <Form.List name="ingredients">
            {(fields, { add, remove }) => (
              <>
                <div
                  style={{
                    background: '#fafafa',
                    border: '1px dashed #d9d9d9',
                    borderRadius: 8,
                    padding: 10,
                    marginBottom: 10,
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Dán nhanh công thức</div>
                  <Input.TextArea
                    rows={3}
                    value={quickPasteText}
                    onChange={(e) => setQuickPasteText(e.target.value)}
                    placeholder={
                      isSingleAmountMode
                        ? 'Mỗi dòng: Tên thành phần[TAB]Định lượng\nVí dụ: Double shot A\t20\nSữa tươi\t190\nKem rich\t15'
                        : 'Mỗi dòng: Tên thành phần[TAB]Nóng[TAB]Đá\nVí dụ: Double shot A\t20\t20\nSữa tươi\t180\t150\nĐá viên\tx\t150'
                    }
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <span style={{ fontSize: 11, color: '#888' }}>
                      Hỗ trợ ngăn cách bằng tab, dấu ;, |, dấu phẩy, hoặc nhiều khoảng trắng.
                    </span>
                    <Space>
                      <Button size="small" onClick={() => setQuickPasteText('')}>
                        Xóa nội dung dán
                      </Button>
                      <Button size="small" type="primary" onClick={handleApplyQuickPasteIngredients}>
                        Áp dụng vào thành phần
                      </Button>
                    </Space>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <b>Thành phần công thức</b>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => add({ name: '', hotAmount: '', icedAmount: '' })}
                  >
                    Thêm thành phần
                  </Button>
                </div>

                {fields.length > 0 && (
                  <Row gutter={8} style={{ marginBottom: 4, fontWeight: 600, fontSize: 12, color: '#666' }}>
                    <Col xs={24} md={isSingleAmountMode ? 16 : 10}><span>Tên thành phần</span></Col>
                    <Col xs={22} md={isSingleAmountMode ? 6 : 6}><span>{isSingleAmountMode ? 'Định lượng (g)' : 'Nóng (g)'}</span></Col>
                    {!isSingleAmountMode && <Col xs={11} md={6}><span>Đá (g)</span></Col>}
                    <Col xs={2} md={2} />
                  </Row>
                )}

                {!fields.length ? (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Chưa có thành phần nào. Bấm Thêm thành phần để bắt đầu."
                  />
                ) : null}

                {fields.map((field) => (
                  <Row gutter={6} key={field.key} align="middle" style={{ marginBottom: 6 }}>
                    <Col xs={24} md={isSingleAmountMode ? 16 : 10}>
                      <Form.Item {...field} name={[field.name, 'name']} style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Nhập tên' }]}>
                        <Input placeholder="Double shot A, Sữa tươi, Đá viên..." />
                      </Form.Item>
                    </Col>
                    <Col xs={22} md={6}>
                      <Form.Item {...field} name={[field.name, 'hotAmount']} style={{ marginBottom: 0 }}>
                        <Input
                          placeholder={isSingleAmountMode ? 'VD: 18, 18ml, 0.5l' : '18g / 18ml hoặc x'}
                        />
                      </Form.Item>
                    </Col>
                    {!isSingleAmountMode && (
                      <Col xs={11} md={6}>
                        <Form.Item {...field} name={[field.name, 'icedAmount']} style={{ marginBottom: 0 }}>
                          <Input placeholder="150g / 150ml hoặc x" />
                        </Form.Item>
                      </Col>
                    )}
                    <Col xs={2} md={2} style={{ textAlign: 'center' }}>
                      <Popconfirm title="Xóa thành phần này?" onConfirm={() => remove(field.name)} okText="Xóa" cancelText="Hủy">
                        <Button danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Col>
                  </Row>
                ))}
              </>
            )}
          </Form.List>

          {/* ─── Thành phần theo option ─────────────────────────────────── */}
          <div style={{ borderTop: '1px solid #f0f0f0', marginTop: 20, paddingTop: 16 }}>
            <Form.List name="optionAddons">
              {(addonFields, { add: addAddon, remove: removeAddon }) => (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <b>Thành phần bổ sung theo option</b>
                    <Space>
                      <Button onClick={handlePrefillOptionsForDish}>
                        Lấy option của món
                      </Button>
                      <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={() => addAddon({ optionId: '', optionName: '', ingredients: [] })}
                      >
                        Thêm option
                      </Button>
                    </Space>
                  </div>

                  {!addonFields.length && (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="Chưa có option nào. Bấm Thêm option nếu món có các lựa chọn bổ sung."
                    />
                  )}

                  {addonFields.map((addonField) => (
                    <div
                      key={addonField.key}
                      style={{ background: '#fafafa', border: '1px solid #e8e8e8', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}
                    >
                      {/* Chọn option + nút xóa trên 1 hàng */}
                      <Row gutter={8} align="middle" style={{ marginBottom: 6 }}>
                        <Col flex="auto">
                          <Form.Item
                            {...addonField}
                            name={[addonField.name, 'optionId']}
                            style={{ marginBottom: 0 }}
                            rules={[{ required: true, message: 'Chọn option' }]}
                          >
                            <Select
                              showSearch
                              allowClear
                              optionFilterProp="label"
                              placeholder="Chọn option theo món"
                              options={selectableOptionItems.map((o) => ({
                                value: o.optionId,
                                label: `${o.optionName} (${o.optionId})`,
                              }))}
                              onChange={(val) => {
                                const found = selectableOptionItems.find((o) => o.optionId === val);
                                if (found) {
                                  const current = (form.getFieldValue('optionAddons') || []) as OptionAddonFormValues[];
                                  if (!current[addonField.name]) return;
                                  current[addonField.name].optionName = found.optionName;
                                  form.setFieldsValue({ optionAddons: current });
                                }
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col flex="none">
                          <Popconfirm title="Xóa option này?" onConfirm={() => removeAddon(addonField.name)} okText="Xóa" cancelText="Hủy">
                            <Button danger size="small" icon={<DeleteOutlined />}>Xóa</Button>
                          </Popconfirm>
                        </Col>
                      </Row>

                      {/* Nguyên liệu bổ sung */}
                      <Form.List name={[addonField.name, 'ingredients']}>
                        {(ingFields, { add: addIng, remove: removeIng }) => (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                              <span style={{ fontSize: 11, color: '#888' }}>Nguyên liệu cộng thêm</span>
                              <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={() => addIng({ name: '', hotAmount: '', icedAmount: '' })}>
                                + Nguyên liệu
                              </Button>
                            </div>

                            {ingFields.length > 0 && (
                              <Row gutter={6} style={{ marginBottom: 2, fontWeight: 600, fontSize: 11, color: '#aaa' }}>
                                <Col md={isSingleAmountMode ? 16 : 10}>Tên nguyên liệu</Col>
                                <Col md={6}>{isSingleAmountMode ? 'Định lượng (g)' : 'Nóng (g)'}</Col>
                                {!isSingleAmountMode && <Col md={6}>Đá (g)</Col>}
                                <Col md={2} />
                              </Row>
                            )}

                            {!ingFields.length && (
                              <div style={{ fontSize: 11, color: '#bbb', marginBottom: 2 }}>Chưa có nguyên liệu</div>
                            )}

                            {ingFields.map((ingField) => (
                              <Row gutter={6} key={ingField.key} align="middle" style={{ marginBottom: 4 }}>
                                <Col xs={24} md={isSingleAmountMode ? 16 : 10}>
                                  <Form.Item {...ingField} name={[ingField.name, 'name']} style={{ marginBottom: 0 }} rules={[{ required: true, message: 'Nhập tên' }]}>
                                    <Input placeholder="Tên nguyên liệu..." />
                                  </Form.Item>
                                </Col>
                                <Col xs={22} md={6}>
                                  <Form.Item {...ingField} name={[ingField.name, 'hotAmount']} style={{ marginBottom: 0 }}>
                                    <Input
                          placeholder={isSingleAmountMode ? 'VD: 18, 18ml, 0.5l' : '18g / 18ml hoặc x'}
                        />
                                  </Form.Item>
                                </Col>
                                {!isSingleAmountMode && (
                                  <Col xs={11} md={6}>
                                    <Form.Item {...ingField} name={[ingField.name, 'icedAmount']} style={{ marginBottom: 0 }}>
                                      <Input placeholder="150g / 150ml hoặc x" />
                                    </Form.Item>
                                  </Col>
                                )}
                                <Col xs={2} md={2} style={{ textAlign: 'center' }}>
                                  <Button danger icon={<DeleteOutlined />} onClick={() => removeIng(ingField.name)} />
                                </Col>
                              </Row>
                            ))}
                          </>
                        )}
                      </Form.List>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </div>

          <Space style={{ marginTop: 8 }}>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving}>
              Lưu công thức
            </Button>
            <Button onClick={closeModal}>Hủy</Button>
          </Space>
        </Form>
      </Modal>
    </AdminLayout>
  );
};

export default RecipeManagement;
